import type { FC } from "react";
import { Navigate } from "react-router-dom";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import {
	useVideoAdminCheck,
	useVideoAdminOverview,
} from "./useVideoGenerations";

/** Story 6.3: Firestarter's internal queue of failed videos, plus BYO volume. */
export const PageVideoAdmin: FC = () => {
	const { data: adminCheck, isLoading: checkLoading } = useVideoAdminCheck();
	const { data, isLoading } = useVideoAdminOverview(
		adminCheck?.isAdmin === true,
	);

	if (checkLoading) {
		return (
			<AppPage title="Video generation">
				<p>Loading...</p>
			</AppPage>
		);
	}

	if (!adminCheck?.isAdmin) {
		return <Navigate to={LinkGen.Settings.Apps()} replace />;
	}

	return (
		<AppPage title="Video generation">
			<div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
				<section>
					<h2 className="h5">Usage by account</h2>
					<p className="text-muted">
						Month starting {data?.periodStart ?? "…"}. Finished minutes made
						through Moxie; HeyGen bills each customer's own credits.
					</p>
					{isLoading || !data ? (
						<p>Loading...</p>
					) : data.usage.length === 0 ? (
						<p className="text-muted">No videos this month.</p>
					) : (
						<div className="table-responsive">
							<table className="table table-sm align-middle">
								<thead>
									<tr>
										<th>Company</th>
										<th className="text-end">Minutes</th>
										<th className="text-end">Videos</th>
										<th className="text-end">Failed</th>
									</tr>
								</thead>
								<tbody>
									{data.usage.map((row) => (
										<tr key={row.companyId}>
											<td>
												{row.companyName}{" "}
												<span className="text-muted">#{row.companyId}</span>
											</td>
											<td className="text-end">{row.minutesUsed}</td>
											<td className="text-end">{row.videos}</td>
											<td className="text-end">{row.failedVideos}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</section>

				<section>
					<h2 className="h5">Failed videos</h2>
					<p className="text-muted">
						Latest 100. Quote the session and video ids to HeyGen support.
					</p>
					{isLoading || !data ? (
						<p>Loading...</p>
					) : data.failures.length === 0 ? (
						<p className="text-muted">No failed videos.</p>
					) : (
						<div className="table-responsive">
							<table className="table table-sm align-middle">
								<thead>
									<tr>
										<th>When</th>
										<th>Company</th>
										<th>Code</th>
										<th>Session</th>
										<th>Video</th>
										<th>Post</th>
										<th>What the user saw</th>
									</tr>
								</thead>
								<tbody>
									{data.failures.map((row) => (
										<tr key={row.id}>
											<td>{new Date(row.failedAt).toLocaleString()}</td>
											<td>
												{row.companyName}{" "}
												<span className="text-muted">#{row.companyId}</span>
											</td>
											<td>{row.failureCode ?? "—"}</td>
											<td>
												<code className="small">{row.sessionId ?? "—"}</code>
											</td>
											<td>
												<code className="small">{row.videoId ?? "—"}</code>
											</td>
											<td>{row.postId ?? "—"}</td>
											<td className="small">{row.failureMessage ?? "—"}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</section>
			</div>
		</AppPage>
	);
};
